import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

import PageTitle from "../../layouts/PageTitle";
import { appApi } from "../../../store/appApi";
import { TENURE_OPTIONS, TYPE_OPTIONS } from "./netplusConfig";
import { createNetplusReceiptApi } from "../../../services/netplus";

const darkPanel =
  "rounded-3xl border border-gray-200 bg-white text-gray-900 shadow-xl shadow-slate-200/70";

const inputClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";

const labelClass =
  "mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-gray-500";

const EMPTY_FORM = {
  date: new Date(),
  type: "Recharge",
  customerId: "",
  customerName: "",
  contact: "",
  receiptNumber: "",
  amount: "",
  tenure: TENURE_OPTIONS[0],
};

const AddNetplusReceipt = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleFormKeyDown = (e) => {
    if (e.key === "Enter" && e.target.type !== "submit") {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.customerId || !form.customerName || !form.receiptNumber) {
      Swal.fire({
        icon: "warning",
        title: "Missing details",
        text: "Customer ID, customer name, and receipt number are required.",
      });
      return;
    }

    const numericAmount = Number(form.amount);
    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Please enter an amount greater than zero.",
      });
      return;
    }

    try {
      setSaving(true);
      await createNetplusReceiptApi({
        date: form.date.toISOString(),
        type: form.type,
        customerId: form.customerId,
        customerName: form.customerName,
        contact: form.contact,
        receiptNumber: form.receiptNumber,
        amount: numericAmount,
        tenure: form.tenure,
      });

      dispatch(appApi.util.invalidateTags(["NetplusReceipts"]));

      Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Receipt recorded successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      setForm(EMPTY_FORM);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to save",
        text:
          error?.response?.data?.message ||
          "Something went wrong while saving this receipt.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Add Receipt" motherMenu="Netplus Receipts" />

      <div className="space-y-6">
        <section className={`${darkPanel} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
              Netplus Receipts
            </div>
            <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
              Add Receipt
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-500">
              Log a customer recharge or new-connection payment. It adds to
              the Netplus balance for this project until transferred to the
              Daybook.
            </p>
            <button
              type="button"
              className="mt-4 rounded-full border-2 border-amber-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
              onClick={() => navigate("/netplus")}
            >
              View All Receipts
            </button>
          </div>
        </section>

        <form
          onSubmit={handleSubmit}
          onKeyDown={handleFormKeyDown}
          className={`${darkPanel} p-5 md:p-6`}
        >
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass}>Date</label>
              <DatePicker
                selected={form.date}
                onChange={(date) => date && updateField("date", date)}
                className={inputClass}
                autoComplete="off"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
              />
            </div>

            <div>
              <label className={labelClass}>Type</label>
              <div className="flex gap-2">
                {TYPE_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => updateField("type", option)}
                    className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                      form.type === option
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : "border-gray-300 bg-white text-gray-600 hover:border-amber-400"
                    }`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelClass}>Customer ID</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={form.customerId}
                onChange={(event) => updateField("customerId", event.target.value)}
                placeholder="External customer reference"
              />
            </div>

            <div>
              <label className={labelClass}>Customer Name</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={form.customerName}
                onChange={(event) => updateField("customerName", event.target.value)}
                placeholder="Customer's full name"
              />
            </div>

            <div>
              <label className={labelClass}>Contact</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={form.contact}
                onChange={(event) => updateField("contact", event.target.value)}
                placeholder="Phone number"
              />
            </div>

            <div>
              <label className={labelClass}>Receipt Number</label>
              <input
                className={inputClass}
                autoComplete="off"
                value={form.receiptNumber}
                onChange={(event) => updateField("receiptNumber", event.target.value)}
                placeholder="Receipt / invoice number"
              />
            </div>

            <div>
              <label className={labelClass}>Amount</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={inputClass}
                autoComplete="off"
                value={form.amount}
                onChange={(event) => updateField("amount", event.target.value)}
                placeholder="0.00"
              />
            </div>

            <div>
              <label className={labelClass}>Tenure</label>
              <select
                className={inputClass}
                autoComplete="off"
                value={form.tenure}
                onChange={(event) => updateField("tenure", Number(event.target.value))}
              >
                {TENURE_OPTIONS.map((months) => (
                  <option key={months} value={months}>
                    {months} month{months === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6 flex justify-end border-t border-gray-200 pt-5">
            <button
              type="submit"
              disabled={saving}
              className="rounded-full bg-amber-500 px-5 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? "Saving..." : "Add Receipt"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddNetplusReceipt;
